import * as Astronomy from "astronomy-engine";

export const ECLIPSE_DATE = new Date("2026-08-12T00:00:00Z");
const SEARCH_START = new Date("2026-08-11T00:00:00Z");

const SUN_RADIUS_KM = 696_000;
const MOON_RADIUS_KM = 1_737.4;
const AU_KM = 149_597_870.7;

export interface ContactEvent {
  time: Date;
  altitude: number;
}

export interface LocalCircumstances {
  visible: boolean;
  onTargetDate: boolean;
  kind: "total" | "annular" | "partial" | "none";
  obscurationPercent: number;
  c1: ContactEvent | null; // partial begins
  c2: ContactEvent | null; // total/annular begins
  max: ContactEvent | null; // peak
  c3: ContactEvent | null; // total/annular ends
  c4: ContactEvent | null; // partial ends
  /** Set when the location doesn't see the 2026-08-12 eclipse but sees some other one soon. */
  nextElsewhere: { time: Date; kind: string } | null;
}

export interface SkyState {
  sunAltitudeDeg: number;
  sunAzimuthDeg: number;
  /** Moon's offset from the Sun as it appears in the sky: +x = right, +y = up. */
  dxDeg: number;
  dyDeg: number;
  sunRadiusDeg: number;
  moonRadiusDeg: number;
  obscurationPercent: number;
  sunUp: boolean;
}

export function makeObserver(lat: number, lon: number, elevationM: number): Astronomy.Observer {
  return new Astronomy.Observer(lat, lon, elevationM);
}

function mapKind(kind: Astronomy.EclipseKind): "total" | "annular" | "partial" {
  switch (kind) {
    case Astronomy.EclipseKind.Total:
      return "total";
    case Astronomy.EclipseKind.Annular:
      return "annular";
    default:
      return "partial";
  }
}

function toContactEvent(e: Astronomy.EclipseEvent | undefined): ContactEvent | null {
  if (!e) return null;
  return { time: e.time.date, altitude: e.altitude };
}

function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

export function getLocalCircumstances(observer: Astronomy.Observer): LocalCircumstances {
  const result = Astronomy.SearchLocalSolarEclipse(SEARCH_START, observer);

  const onTargetDate = isSameUtcDay(result.peak.time.date, ECLIPSE_DATE);

  if (!onTargetDate) {
    return {
      visible: false,
      onTargetDate: false,
      kind: "none",
      obscurationPercent: 0,
      c1: null,
      c2: null,
      max: null,
      c3: null,
      c4: null,
      nextElsewhere: { time: result.peak.time.date, kind: result.kind },
    };
  }

  return {
    visible: true,
    onTargetDate: true,
    kind: mapKind(result.kind),
    obscurationPercent: result.obscuration * 100,
    c1: toContactEvent(result.partial_begin),
    c2: toContactEvent(result.total_begin),
    max: toContactEvent(result.peak),
    c3: toContactEvent(result.total_end),
    c4: toContactEvent(result.partial_end),
    nextElsewhere: null,
  };
}

/** Real-time (or scrubbed-time) Sun/Moon geometry as it would actually appear in the sky. */
export function getSkyState(observer: Astronomy.Observer, when: Date): SkyState {
  const sunEq = Astronomy.Equator(Astronomy.Body.Sun, when, observer, true, true);
  const moonEq = Astronomy.Equator(Astronomy.Body.Moon, when, observer, true, true);

  const sunHor = Astronomy.Horizon(when, observer, sunEq.ra, sunEq.dec, "normal");
  const moonHor = Astronomy.Horizon(when, observer, moonEq.ra, moonEq.dec, "normal");

  const sunRadiusDeg = (Math.asin(SUN_RADIUS_KM / (sunEq.dist * AU_KM)) * 180) / Math.PI;
  const moonRadiusDeg = (Math.asin(MOON_RADIUS_KM / (moonEq.dist * AU_KM)) * 180) / Math.PI;

  let daz = moonHor.azimuth - sunHor.azimuth;
  daz = ((daz + 180) % 360 + 360) % 360 - 180;
  const dxDeg = daz * Math.cos((sunHor.altitude * Math.PI) / 180);
  const dyDeg = moonHor.altitude - sunHor.altitude;

  const separationDeg = Math.hypot(dxDeg, dyDeg);
  const obscurationPercent = obscuration(separationDeg, sunRadiusDeg, moonRadiusDeg) * 100;

  return {
    sunAltitudeDeg: sunHor.altitude,
    sunAzimuthDeg: sunHor.azimuth,
    dxDeg,
    dyDeg,
    sunRadiusDeg,
    moonRadiusDeg,
    obscurationPercent,
    sunUp: sunHor.altitude > 0,
  };
}

/** Fraction (0-1) of the Sun's disc area covered by the Moon, given center separation and radii (same units). */
function obscuration(d: number, rSun: number, rMoon: number): number {
  if (d >= rSun + rMoon) return 0;
  if (d <= Math.abs(rSun - rMoon)) return rMoon >= rSun ? 1 : (rMoon / rSun) ** 2;

  const a = rSun ** 2 * Math.acos((d ** 2 + rSun ** 2 - rMoon ** 2) / (2 * d * rSun));
  const b = rMoon ** 2 * Math.acos((d ** 2 + rMoon ** 2 - rSun ** 2) / (2 * d * rMoon));
  const c = 0.5 * Math.sqrt(
    Math.max(0, (-d + rSun + rMoon) * (d + rSun - rMoon) * (d - rSun + rMoon) * (d + rSun + rMoon))
  );
  return (a + b - c) / (Math.PI * rSun ** 2);
}
